/* eslint-disable max-len */
import { FunctionComponent, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CircularProgress, FormControl } from '@material-ui/core';
import { useMutation } from '@apollo/client';

import useGetQueryParams from '../../customHooks/useGetQueryParams';
import { encryptData } from '../../utils/encrypt';
import ModalWrapComp from '../../components/ModalWrapComp';
import { PasswordChangeSuccessModal } from './_partials';
import TextInput from '../../components/input';
import useForm from '../../customHooks/useForm';
import AdminAuthWrapper from '../../components/AdminAuthWrapper';
import { CHANGE_PASSWORD } from '../../graphql/admin_mutations';
import useToast from '../../customHooks/useToast';
import { IPasswordChangeRequest } from './PasswordChangeNew.types';

import PasswordShield from '../../svgs/PasswordShield';

import styles from './PasswordChange.module.scss';

const PasswordChange: FunctionComponent = () => {
  const { push } = useHistory();
  const { getQuery } = useGetQueryParams();
  const resetToken = getQuery('resetToken');
  const [closeModal, setCloseModal] = useState(false);
  const [changePassword] = useMutation<
    { adminResetPassword: any; changePasswordInfo: any },
    any
  >(CHANGE_PASSWORD);
  const [loading, setLoading] = useState(false);
  const Toast = useToast();

  // regex pattern for password
  // (lower case, upper case, number, special character, minimum 8 characters)
  const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
  const errorMessage = 'Password must contain at least 8 characters, 1 uppercase, 1 lowercase, 1 number and 1 special character';
  const {
    formValues, handleChange, checkAllRequired, validateInputs, resetForm,
  } = useForm({
    newPassword: '',
    confirmPassword: '',
  }, {
    newPassword: {
      pattern,
      errorMessage,
    },
    confirmPassword: {
      pattern,
      errorMessage,
    },
  }, Toast);

  const handleOnSubmit: (arg: any) => void = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!checkAllRequired()) {
        setLoading(false);
        Toast.error('Please complete all fields');
        return;
      }
      if (formValues?.newPassword !== formValues?.confirmPassword) {
        setLoading(false);
        Toast.error('Passwords must match');
        return;
      }
      if (!validateInputs()) {
        setLoading(false);
        return;
      }
      const payload: IPasswordChangeRequest = {
        resetToken,
        newPassword: formValues?.newPassword,
      };

      const reqPayload = await encryptData(JSON.stringify(payload));
      const resp = await changePassword({
        variables: {
          data: reqPayload,
        },
      });
      setLoading(false);
      if (
        resp?.data?.adminResetPassword
        && resp?.data?.adminResetPassword?.status === 200
      ) {
        Toast.success('Password successfully changed. Login here');
        resetForm();
        setCloseModal(true);
      } else {
        Toast.error(
          resp?.data?.adminResetPassword?.message
            || 'Something went wrong. Try again.',
        );
      }
    } catch (e) {
      setLoading(false);
      if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
        Toast.error(e.graphQLErrors[0].message);
      } else {
        console.log('e', e);
        Toast.error('Something went wrong. Try again or Contact Support');
      }
    }
  };

  useEffect(() => {
    if (!resetToken) {
      Toast.error('Invalid reset password link');
      push('/');
    }
  }, [resetToken]);

  return (
    <AdminAuthWrapper handleOnSubmit={handleOnSubmit} headText="Set New Password">
      <div className={styles?.passwordChange}>
        <form className={styles?.passwordChange__form} action="" onSubmit={handleOnSubmit}>
          <FormControl className={styles?.passwordChange__content}>
            <TextInput
              label="New Password (8+ characters)"
              isSecure
              width="100%"
              customOnChange={handleChange}
              name="newPassword"
              value={formValues.newPassword}
              preIcon={<PasswordShield />}
              paddingLeft="44px"
            />
            <TextInput
              label="Confirm Password (8+ characters)"
              isSecure
              width="100%"
              customOnChange={handleChange}
              name="confirmPassword"
              value={formValues.confirmPassword}
              preIcon={<PasswordShield />}
              paddingLeft="44px"
            />
            <button className={`mb-2 bg-primary text-white rounded ${styles?.passwordChange__btn}`}>
              {loading ? (
                <CircularProgress color="inherit" size={15} />
              ) : (
                'Save new password'
              )}

            </button>
          </FormControl>
        </form>
        <div className={styles?.passwordChange__create}>
          Remember your password?
          {' '}
          <Link className={styles?.passwordChange__link} to="/"><span className={styles?.passwordChange__createText}>Log in</span></Link>
        </div>
        {
        // eslint-disable-next-line max-len
          closeModal && <ModalWrapComp closeModal={() => setCloseModal(false)}><PasswordChangeSuccessModal /></ModalWrapComp>

        }

      </div>
    </AdminAuthWrapper>
  );
};

export default PasswordChange;
